import React from 'react'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Avatar from '@material-ui/core/Avatar'
import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined'
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined'
import { currency } from 'lib/utils/string'
import { centsToDollars } from 'lib/utils/number'
import Dialog from 'components/dialog'
import AccountRouterLink from 'components/account-router-link'
import RecordBookingPaymentDialog from 'components/record-booking-payment-dialog'
import {
  toFullTextDayDate,
  toFullTextDate,
  getTimePeriodWithTimezone,
} from 'utils/dateUtils'
import useIconAccessor from 'components/lesson-type-category/icons'
import ActionButtons from './action-buttons'
import EnrollmentItem from './enrollment-item'
import { useStyles } from './styles'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'

const SessionDetailsDialogContent = ({
  booking,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const {
    title,
    startDateTime,
    endDateTime,
    coachFacility,
    enrollments,
    isCanceled,
    lessonType,
    timezone,
  } = booking

  const isPaid = enrollments.some(e => e.transactions.some(t => !t.refund))
  const facilityAddress = [
    coachFacility?.city && `${coachFacility.city},`,
    coachFacility?.state,
    coachFacility?.zipCode,
  ]
    .filter(s => !!s)
    .join(' ')

  const enrollment = enrollments[0]
  const paymentAmount =
    (isPaid && enrollment?.transactions[0]?.amountInCents) || 0
  const paymentDate = isPaid && enrollment?.transactions[0]?.createdAt
  const registrationPaymentId =
    isPaid && enrollment?.transactions[0]?.registrationPayment.id
  const student = enrollment?.student
  const studentName = `${student?.firstName} ${student.lastName}`

  const classes = useStyles()
  const accessor = useIconAccessor()

  return (
    <>
      <DialogTitle className={classes.dialogTitle}>
        <Box position="relative">
          <ActionButtons
            booking={booking}
            onReschedule={onReschedule}
            onCancellation={onCancellation}
          />
          <Avatar className={classes.categoryIcon}>
            {accessor[lessonType.category].icon}
          </Avatar>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mt={4} p={1.5}>
          <Box mb={2}>
            <Typography style={{ fontSize: '30px', fontWeight: 900 }}>
              {title}
            </Typography>
          </Box>
          {coachFacility ? (
            <Box display="flex" alignItems="center" mb={3}>
              <Box mr={4} color="text.secondary">
                <RoomOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="body1">{coachFacility.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {facilityAddress}
                </Typography>
              </Box>
            </Box>
          ) : null}
          <Box display="flex" alignItems="center" mb={3}>
            <Box mr={4} color="text.secondary">
              <EventNoteOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="body1">
                {toFullTextDayDate(startDateTime)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {getTimePeriodWithTimezone(
                  startDateTime,
                  endDateTime,
                  timezone,
                )}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" mb={3}>
            <Box mr={4} color="text.secondary">
              <MonetizationOnOutlinedIcon />
            </Box>
            <Box
              display="flex"
              width="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body1">
                {isPaid
                  ? `${currency(
                    centsToDollars(paymentAmount),
                  )} on ${toFullTextDate(paymentDate)}`
                  : 'Pending Payment'}
              </Typography>
              <SensitiveInfoWrapper>
                {isPaid ? (
                  <Button
                    color="primary"
                    component={AccountRouterLink}
                    to={`/account/payments/transactions/${registrationPaymentId}`}
                  >
                    View Receipt
                  </Button>
                ) : !isCanceled ? (
                  <RecordBookingPaymentDialog
                    trigger={<Button color="primary">Mark As Paid</Button>}
                    enrollmentId={enrollment?.id}
                    eventTitle={title}
                    eventDate={startDateTime}
                    participantName={studentName}
                    defaultAmountInCents={lessonType.priceInCents}
                    acceptOnlinePayments={lessonType.acceptsOnlinePayments}
                    onPaymentSuccess={onPayment}
                  />
                ) : null}
              </SensitiveInfoWrapper>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" mb={3}>
            <Box mr={4} color="text.secondary">
              <PeopleOutlinedIcon />
            </Box>
            <Typography variant="body1">Attendees</Typography>
          </Box>
          <Box display="flex" alignItems="center" mb={3}>
            {enrollment && <EnrollmentItem enrollment={enrollment} />}
          </Box>
        </Box>
      </DialogContent>
    </>
  )
}

const SessionDetailsDialog = ({
  isOpen,
  setIsOpen,
  booking,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  return (
    <Dialog
      maxWidth="sm"
      fullScreen={isMobile}
      fullWidth={true}
      isOpen={isOpen}
      showCloseButton
      onClose={() => setIsOpen(!isOpen)}
    >
      {() => (
        <SessionDetailsDialogContent
          booking={booking}
          onReschedule={onReschedule}
          onPayment={onPayment}
          onCancellation={onCancellation}
        />
      )}
    </Dialog>
  )
}

export default SessionDetailsDialog
